import { enqueueSnackbar } from 'notistack';
import { handleErrors } from '../../../../utils/common';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks/reduxHooks';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
    useCreateEstablishmentMutation,
    useGetCIIUCodesQuery,
    useGetCIIURevisionsQuery,
    useGetEstablishmentDetailQuery,
    useGetProvincesQuery,
    useLazyGetCitiesQuery
} from '../../../../services/companyApi';
import { useTranslation } from 'react-i18next';
import useFindActivityTypeByCIIUV4 from '../../hooks/useFindActivityTypeByCIIUV4';
import { EstablishmentTypeOption } from '../../consts/types';

type Organism = {
    id: number;
    nombre: string;
};

interface CategoriaFiscal {
    id: string;
    nombre: string;
    codigo: string;
}

interface Direccion {
    idCiudad: number;
    idCP: number;
    calle: string;
    altura: string;
    piso: string;
    departamento: string;
    latitud: string;
    longitud: string;
    interseccion?: string | null;
    cpa?: string | null;
}

export interface CreateEstablishmentBody {
    idOrganismo: number;
    nombre: string;
    idCIIU: number | null;
    direccion: Omit<Direccion, 'idCP' | 'idCiudad'> & {
        idLocalidad: number;
    };
    codigoSRT?: string | null;
    cuit?: number | null;
    email?: string | null;
    telefono: string;
    cantidadEmpleados: number;
    observaciones: string;
    idTipoActividad?: number | null;
    tipoEstablecimiento?: number | null;
    propio?: number | null;
    principal?: boolean | null;
}

interface Perfiles {
    id: string;
    nombre: string;
}

type ValueProps = {
    options: [];
    id?: number;
    nombre?: string;
};

const valueFormInitialState = {
    organizacion: null,
    nombre: '',
    idCIIU: null,
    codigoSRT: '',
    direccion: {
        idCiudad: null,
        idCP: null,
        calle: '',
        altura: '',
        piso: '',
        departamento: '',
        latitud: '',
        longitud: '',
        interseccion: '',
        cpa: ''
    },
    email: '',
    telefono: '',
    cantidadEmpleados: '',
    observaciones: ''
};

function useEstablishmentSave() {
    const [dataFiscal, setDataFiscal] = useState<CategoriaFiscal | null>(null);
    const [dataPerfiles, setDataPerfiles] = useState<Perfiles[]>([]);
    const [errors, setErrors] = useState<any>(null);
    const [propertyData, setPropertyData] = useState<any>({ idCiudad: 0, idPais: 0 });
    const [selectedCIIU, setSelectedCIIU] = useState<{ id: number; codigo: number } | null>(null);
    const [selectedProvince, setSelectedProvince] = useState<{ id: number; nombre: string } | null>(
        null
    );
    const [selectedCity, setSelectedCity] = useState<{ id: number; nombre: string } | null>(null);
    const [selectedPostalCode, setSelectedPostalCode] = useState<number | null>(null);
    const [selectedOrganism, setSelectedOrganism] = useState<Organism | null>(null);
    const { data: establishmentDetail } = useGetEstablishmentDetailQuery({ establishmentId: 4 });
    const { data: provincesList, isLoading: loadingProvinces } = useGetProvincesQuery(null);
    const [getCities, { data: citiesList, isLoading: loadingCities }] = useLazyGetCitiesQuery();
    const { data: ciiuCodes } = useGetCIIUCodesQuery(null);
    const { data: ciiuRevisionsData } = useGetCIIURevisionsQuery(null);
    const [noPersonnel, setNoPersonnel] = useState<boolean>(false);
    const [propio, setPropio] = useState<boolean>(false);
    const [principal, setPrincipal] = useState<boolean>(false);
    const [selectedTipoEstablecimiento, setSelectedTipoEstablecimiento] =
        useState<EstablishmentTypeOption | null>(null);
    const userData = useAppSelector((state) => state.auth.user);
    const { t } = useTranslation('establishments', { keyPrefix: 'newEstablishmentForm' });
    const [latitudeError, setLatitudeError] = useState('');
    const [longitudeError, setLongitudeError] = useState('');

    const { getActivityTypeByCIIUV4 } = useFindActivityTypeByCIIUV4();

    const ciiuRev4Code = useMemo(() => {
        if (!selectedCIIU?.codigo) return '';
        const match = ciiuRevisionsData?.data?.codigos?.find(
            (item: any) => item.ciiuRev3 === selectedCIIU.codigo.toString()
        );
        return match?.ciiuRev4 ?? '';
    }, [selectedCIIU?.codigo, ciiuRevisionsData]);

    const activityTypeByCIIU = getActivityTypeByCIIUV4(ciiuRev4Code);

    const handleProvinceSelection = (province: { id: number; nombre: string }) => {
        if (!province?.id) return;
        getCities({ provinceId: province.id });
        setSelectedProvince(province);
    };

    const handleCitySelection = (city: { id: number; nombre: string; codigoPostal: number }) => {
        setSelectedCity(city);
        handlePostalCodeSelection(city?.codigoPostal);
    };

    const handlePostalCodeSelection = (postalCode: any) => {
        setSelectedPostalCode(postalCode);
    };

    const handleCIIUSelection = (ciiu: { id: number; codigo: number }) => {
        setSelectedCIIU(ciiu);
    };

    const toggleNoPersonnel = () => {
        setNoPersonnel((prevState) => !prevState);
    };

    const handleOrganismSelection = (organism: Organism) => {
        if (organism?.id && organism?.nombre) {
            setSelectedOrganism(organism);
        } else {
            setSelectedOrganism(null);
        }
    };

    const handleTipoEstablecimientoSelection = (option: EstablishmentTypeOption | null) => {
        setSelectedTipoEstablecimiento(option);
    };

    const togglePropio = () => {
        setPropio((prev) => !prev);
    };

    const togglePrincipal = () => {
        setPrincipal((prev) => !prev);
    };

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [createEstablishment, { isLoading: creatingEstablishment }] =
        useCreateEstablishmentMutation();
    const [valuesForm, setValuesForm] = useState(valueFormInitialState);

    useEffect(() => {
        if (!selectedProvince) return;
        setSelectedCity(null);
        setSelectedPostalCode(null);
        getCities({ provinceId: selectedProvince?.id || '' });
    }, [selectedProvince]);

    const removeKey = (key: string) => {
        let auxObject = { ...errors };
        delete auxObject?.[key];
        setErrors(auxObject);
    };

    const handleChange = (event: any) => {
        event.preventDefault();

        const name = event.target.name;
        const value = event.target.value;

        const updateNestedState = (obj: any, path: string[], value: any): any => {
            if (path.length === 1) {
                return { ...obj, [path[0]]: value };
            }

            return {
                ...obj,
                [path[0]]: updateNestedState(obj[path[0]], path.slice(1), value)
            };
        };

        if (name === 'telefono') {
            if (validatePhoneNumber(value)) {
                setErrors({
                    ...errors,
                    [name]: {
                        ...errors?.[name],
                        msg: 'Formato incorrecto'
                    }
                });
            } else {
                removeKey(name);
            }
        }

        const namePath = name.split('.'); // Split the name by dot notation
        const updatedForm = updateNestedState(valuesForm, namePath, value);

        setValuesForm(updatedForm);
    };

    const validatePhoneNumber = (number: string) => isNaN(+number);

    const getValue = ({ options, id, nombre }: ValueProps) =>
        options?.find((i: any) => i.id === id || i.nombre === nombre) || null;

    const isSubmitButtonDisabled = () =>
        !selectedOrganism?.id ||
        !selectedCIIU?.id ||
        !selectedCity?.id ||
        !selectedOrganism ||
        valuesForm.nombre === '' ||
        valuesForm.direccion.calle === '' ||
        valuesForm.direccion.altura === '' ||
        (!noPersonnel && valuesForm.cantidadEmpleados === '') ||
        latitudeError !== '' ||
        longitudeError !== '';

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        // const idPerfiles = dataPerfiles?.map((obj) => obj.id);
        if (
            !selectedOrganism?.id ||
            !selectedCIIU?.id ||
            !selectedCity?.id ||
            !selectedOrganism ||
            valuesForm.nombre === '' ||
            valuesForm.direccion.calle === '' ||
            valuesForm.direccion.altura === '' ||
            (!noPersonnel && valuesForm.cantidadEmpleados === '')
        ) {
            enqueueSnackbar(t('validations.requiredFields'), { variant: 'error' });
            return false;
        }

        if (validatePhoneNumber(valuesForm?.telefono)) {
            enqueueSnackbar(t('validations.invalidPhone'), { variant: 'error' });
            return false;
        }

        const cpa = valuesForm.direccion.cpa;
        if (cpa && !/^[A-Za-z]\d{4}[A-Za-z]{3}$/.test(cpa)) {
            enqueueSnackbar(
                'El CPA debe tener el formato: 1 letra, 4 dígitos y 3 letras (ej: C1024CWN).',
                { variant: 'error' }
            );
            return false;
        }
        if (
            !selectedOrganism?.id ||
            !selectedCIIU?.id ||
            !selectedCity?.id ||
            selectedPostalCode === null
        )
            return;

        const organismId: number = selectedOrganism.id;
        const ciiuID: number = selectedCIIU.id;

        const body: CreateEstablishmentBody = {
            idOrganismo: organismId,
            nombre: valuesForm.nombre,
            idCIIU: ciiuID,
            idTipoActividad: activityTypeByCIIU?.id,
            codigoSRT: valuesForm.codigoSRT,
            direccion: {
                idLocalidad: selectedCity?.id,
                calle: valuesForm.direccion.calle,
                altura: valuesForm.direccion.altura,
                piso: valuesForm.direccion.piso,
                departamento: valuesForm.direccion.departamento,
                latitud: valuesForm.direccion.latitud || '',
                longitud: valuesForm.direccion.longitud || '',
                interseccion: valuesForm.direccion.interseccion || null,
                cpa: valuesForm.direccion.cpa || null
            },
            email: valuesForm.email ? valuesForm.email : null,
            telefono: valuesForm.telefono,
            cantidadEmpleados: noPersonnel ? 0 : Number(valuesForm.cantidadEmpleados),
            observaciones: valuesForm.observaciones,
            tipoEstablecimiento: selectedTipoEstablecimiento?.id || null,
            propio: propio ? 1 : 0,
            principal
        };

        try {
            const response: any = await createEstablishment(body);
            if (response?.statusCode > 400 || response?.error) {
                handleErrors(response?.error);
            } else {
                dispatch(
                    showAlert({
                        title: t('alerts.success.title'),
                        confirmText: t('alerts.success.confirmText'),
                        confirmAction: () => navigate(-1)
                    })
                );
            }
        } catch (error) {
            console.log(error);
            enqueueSnackbar(t('alerts.failure.title'), { variant: 'error' });
            return false;
        }
    };

    const validateLatitude = (value: string) => {
        if (value === '') {
            setLatitudeError('');
            return;
        }
        const lat = parseFloat(value);
        if (isNaN(lat) || lat < -55.407886 || lat > -21.347331) {
            setLatitudeError(t('validations.invalidLatitude') as string);
        } else {
            setLatitudeError('');
        }
    };

    const validateLongitude = (value: string) => {
        if (value === '') {
            setLongitudeError('');
            return;
        }
        const lon = parseFloat(value);
        if (isNaN(lon) || lon < -74.14912 || lon > -53.550551) {
            setLongitudeError(t('validations.invalidLongitude') as string);
        } else {
            setLongitudeError('');
        }
    };

    return {
        dataFiscal,
        dataPerfiles,
        valuesForm,
        creatingEstablishment,
        errors,
        propertyData,
        establishmentDetail,
        provincesList,
        citiesList,
        ciiuCodes,
        selectedProvince,
        selectedCity,
        selectedPostalCode,
        selectedCIIU,
        userData,
        selectedOrganism,
        noPersonnel,
        propio,
        principal,
        selectedTipoEstablecimiento,
        loadingProvinces,
        loadingCities,
        activityTypeByCIIU,
        isSubmitButtonDisabled,
        handleOrganismSelection,
        handleCIIUSelection,
        handleProvinceSelection,
        handleCitySelection,
        handlePostalCodeSelection,
        handleTipoEstablecimientoSelection,
        toggleNoPersonnel,
        togglePropio,
        togglePrincipal,
        setPropertyData,
        setErrors,
        setDataFiscal,
        setDataPerfiles,
        handleChange,
        handleSubmit,
        setValuesForm,
        getValue,
        latitudeError,
        longitudeError,
        validateLatitude,
        validateLongitude
    };
}

export default useEstablishmentSave;
